import {Box, Group, Stack, Switch, Text, TextInput} from '@mantine/core';
import assert from 'assert';
import produce from 'immer';
import React from 'react';

import {FILTER_PREDICATES_BY_MEASUREMENT_TYPE, MeasurementType} from 'samples/MeasurementForm';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';
import {HydratedUnit} from 'units/util';
import {NumericInputChangeEvent} from 'util/Types';

import {
  useConcentrationLayerActorRef,
  useConcentrationLayerSelector,
} from '../concentration-layers/ConcentrationLayerMachineProvider';
import {
  ConcentrationLayerData,
  DilutionSettings as DilutionSettingsType,
} from '../concentration-layers/machine/ConcentrationLayerMachine';
import {PlateMap} from '../PlateMapConstants';
import {calculateWellConcentrations} from '../PlateMapUtil';
import DilutionSettings from './DilutionSettings';

export interface ConcentrationSettingsProps {
  /** The active layer to configure. */
  activeLayer: ConcentrationLayerData | null;
  /** Callback functions */
  onUpdateLayerName: (layerId: string, name: string) => void;
  onUpdateLayerConcentration: (layerId: string, concentration: number | null) => void;
  onUpdateLayerUnit: (layerId: string, unit: HydratedUnit | null) => void;
  onUpdateLayerAllowSetDilutionSettings: (layerId: string, allowSetDilutionSettings: boolean) => void;
  onUpdateLayerDilutionSettings: (layerId: string, settings: Partial<DilutionSettingsType>) => void;
  onApply: () => void;
}

/**
 * Determines if the apply button should be disabled based on the active layer's settings.
 *
 * The apply button should be disabled if:
 * - The concentration is not set
 * - The concentration unit is not set
 * - The dilution factor is not set or is less than or equal to 0
 * - The replicates are not set or are less than 1 or are not an integer
 *
 * @param activeLayer - The active layer to check.
 * @returns True if the apply button should be disabled, false otherwise.
 */
function isApplyButtonDisabled(activeLayer: ConcentrationLayerData): boolean {
  const {concentration, concentrationUnit, allowSetDilutionSettings, dilutionSettings} = activeLayer;
  if (concentration == null || concentrationUnit == null) {
    return true;
  }
  if (allowSetDilutionSettings) {
    const {dilutionFactor, replicates} = dilutionSettings;
    if (dilutionFactor == null || dilutionFactor <= 0) {
      return true;
    }
    if (replicates == null || replicates < 1 || !Number.isInteger(replicates)) {
      return true;
    }
  }
  return false;
}

/**
 * Settings component for configuring concentration layers in plate maps.
 *
 * Allows users to:
 * - Set a name for the concentration layer
 * - Define concentration values and units
 * - Optionally define dilution settings including dilution factor, dilution direction, and replicates
 */
export default function ConcentrationLayerSettings({
  activeLayer,
  onUpdateLayerName,
  onUpdateLayerConcentration,
  onUpdateLayerUnit,
  onUpdateLayerAllowSetDilutionSettings,
  onUpdateLayerDilutionSettings,
  onApply,
}: ConcentrationSettingsProps): JSX.Element {
  assert(activeLayer != null, 'Expected active layer to be defined.');
  return (
    <Stack spacing="sm">
      <Text size="sm" c="dimmed">
        Select wells in the plate to define concentration values.
      </Text>
      <TextInput
        label="Name"
        labelProps={{fw: 'bold'}}
        autoFocus={true}
        onChange={(e) => {
          onUpdateLayerName(activeLayer.layerId, e.target.value);
        }}
        placeholder="Name"
        value={activeLayer.name}
      />
      <Box>
        <FieldLabel label="Units" isRequired={true} />
        <SearchableUnitDropdown
          selectedId={activeLayer.concentrationUnit?.id}
          unitTypeFilter={FILTER_PREDICATES_BY_MEASUREMENT_TYPE[MeasurementType.CONCENTRATION]}
          displaySelectionAsSymbol={true}
          onSelect={(unit) => {
            onUpdateLayerUnit(activeLayer.layerId, unit);
          }}
        />
      </Box>
      <Box>
        <FieldLabel label="Concentration" isRequired={true} />
        <NumericInput
          value={activeLayer.concentration ?? ''}
          min={0}
          onChange={(e: NumericInputChangeEvent) => {
            onUpdateLayerConcentration(activeLayer.layerId, e.target.numericValue);
          }}
          isDisabled={false}
          required={true}
          validateInteger={false}
        />
      </Box>
      <Switch
        label="Set dilution"
        styles={{label: {fontWeight: 'bold'}}}
        checked={activeLayer.allowSetDilutionSettings}
        onChange={() => {
          onUpdateLayerAllowSetDilutionSettings(activeLayer.layerId, !activeLayer.allowSetDilutionSettings);
        }}
      />
      {activeLayer.allowSetDilutionSettings && (
        <DilutionSettings
          activeLayer={activeLayer}
          onUpdateLayerDilutionSettings={onUpdateLayerDilutionSettings}
        />
      )}
      <Group ml="auto">
        <Button
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyle.PRIMARY}
          type="button"
          onClick={onApply}
          disabled={isApplyButtonDisabled(activeLayer)}
        >
          Apply
        </Button>
      </Group>
    </Stack>
  );
}

export interface ProvidedConcentrationLayerSettingsProps {
  /** Current plate map */
  plateMap: PlateMap;
  /** Function to update the plate map */
  onEditPlateMap: (plateMap: PlateMap) => void;
  /** Currently selected well indices */
  selectedWellIndices: Set<number>;
  /** Function to deselect wells */
  deselectWells: () => void;
}

/**
 * A wrapper component that connects ConcentrationLayerSettings to the concentration layer machine context.
 * This component eliminates the need to thread concentration layer props through multiple component layers.
 */
export function ProvidedConcentrationLayerSettings({
  plateMap,
  onEditPlateMap,
  selectedWellIndices,
  deselectWells,
}: ProvidedConcentrationLayerSettingsProps): JSX.Element | null {
  const actor = useConcentrationLayerActorRef();

  const {layers, activeLayerId} = useConcentrationLayerSelector((state) => state.context);
  const activeLayer = activeLayerId ? (layers.get(activeLayerId) ?? null) : null;

  // Don't render if there's no active layer
  if (activeLayer == null) {
    return null;
  }

  function handleApply(): void {
    const concentrationsByWell = calculateWellConcentrations(
      selectedWellIndices,
      plateMap.size,
      activeLayer!
    );
    const updatedPlateMap = produce(plateMap, (draft) => {
      draft.wells.forEach((well, index) => {
        const calculatedConcentration = concentrationsByWell[index];
        if (calculatedConcentration != null) {
          well.concentrationLayers = well.concentrationLayers ?? {};
          well.concentrationLayers[activeLayer!.layerId] = calculatedConcentration;
        }
      });
    });

    onEditPlateMap(updatedPlateMap);
    deselectWells();
  }

  return (
    <ConcentrationLayerSettings
      activeLayer={activeLayer}
      onUpdateLayerName={(layerId: string, name: string) => {
        actor.send({
          type: 'updateLayerName',
          layerId,
          name,
        });
      }}
      onUpdateLayerConcentration={(layerId: string, concentration: number | null) => {
        actor.send({
          type: 'updateLayerConcentration',
          layerId,
          concentration,
        });
      }}
      onUpdateLayerUnit={(layerId: string, unit: HydratedUnit | null) => {
        actor.send({
          type: 'updateLayerUnit',
          layerId,
          unit,
        });
      }}
      onUpdateLayerAllowSetDilutionSettings={(layerId: string, allowSetDilutionSettings: boolean) => {
        actor.send({
          type: 'updateLayerAllowSetDilutionSettings',
          layerId,
          allowSetDilutionSettings,
        });
      }}
      onUpdateLayerDilutionSettings={(layerId: string, settings: Partial<DilutionSettingsType>) => {
        actor.send({
          type: 'updateLayerDilutionSettings',
          layerId,
          settings,
        });
      }}
      onApply={handleApply}
    />
  );
}
